import { Outlet, createRootRouteWithContext, createRoute, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { NotFoundPage } from '@/app/routes/NotFoundPage';
import { currentUserQueryOptions } from '@/lib/current-user';
import { isSignedIn } from '@/lib/session';

/**
 * Router context — available to every route's `beforeLoad` and `loader`.
 *
 * The query client is passed in (rather than imported) so route loaders can
 * prefetch into the same cache the components read from.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

/**
 * Pathless layout route for signed-out screens.
 *
 * Pathless (`id`, no `path`) so the URLs stay clean — `/login`, not
 * `/auth/login`.
 */
export const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  component: AuthLayout,
  beforeLoad: () => {
    // An already-signed-in user landing on /login should go to the app, not
    // sign in a second time.
    if (isSignedIn()) {
      throw redirect({ to: '/' });
    }
  },
});

/**
 * Pathless layout route for screens reachable in EITHER state.
 *
 * Agency activation and invitation acceptance arrive as emailed links, and the
 * person clicking one may already be signed in — a teammate at another agency,
 * or someone who never signed out on this machine. Under `_auth` they would be
 * bounced silently to `/`, which looks like the link is broken. These pages
 * therefore carry no guard and instead show who is signed in, offering to
 * continue or sign out.
 *
 * Shares `AuthLayout` because the chrome is the same; only the guard differs.
 */
export const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_public',
  component: AuthLayout,
});

/**
 * Pathless layout route for everything behind authentication.
 *
 * Guarding here rather than per-route means a new module cannot accidentally
 * ship unguarded: attaching to this parent is what gives a route the app shell,
 * so forgetting the guard would also mean forgetting the layout, which is
 * immediately visible.
 */
export const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppLayout,
  beforeLoad: async ({ location, context }) => {
    if (!isSignedIn()) {
      throw redirect({
        to: '/login',
        // Preserve where they were headed so sign-in can return them there.
        search: { next: location.href },
      });
    }

    // Subscription gate. An agency that has not chosen a plan, whose renewal
    // failed, or whose cancellation ran out gets 402 from every endpoint but
    // billing — so the only screen worth rendering is /billing. Read from the
    // same cached profile the header uses; a failed read (expired token) is
    // left to the 401 handler and does not block navigation here.
    const me = await context.queryClient
      .ensureQueryData({ ...currentUserQueryOptions(), revalidateIfStale: true })
      .catch(() => null);
    if (me?.subscription?.access === 'blocked' && !location.pathname.startsWith('/billing')) {
      throw redirect({ to: '/billing' });
    }
  },
});
